import type { CSSProperties } from 'vue'
import type {
  WChartPopoverIcon,
  WChartPopoverMetric,
  WChartPopoverProps,
  WChartPopoverValueType,
  WChartPopoverVariant,
} from './WChartPopover.vue'

/**
 * Style applied to the `<Teleport to="body">` wrapper around ChartPopover in
 * every chart component. Positions the popover at the supplied viewport
 * coordinate, centers it horizontally, offsets it from that anchor with
 * breathing room, and disables pointer events so the popover can't intercept
 * mouseout from the chart underneath.
 */
export type ChartPopoverPlacement = 'top' | 'bottom'

export const CHART_POPOVER_GAP = 12
export const CHART_POPOVER_POINT_GAP = 24

export function chooseChartPopoverPlacement(
  availableAbove: number,
  availableBelow: number,
  requiredSpace: number,
): ChartPopoverPlacement {
  const hasEnoughAbove = availableAbove >= requiredSpace
  const hasEnoughBelow = availableBelow >= requiredSpace

  if (hasEnoughAbove !== hasEnoughBelow) {
    return hasEnoughAbove ? 'top' : 'bottom'
  }

  return availableAbove >= availableBelow ? 'top' : 'bottom'
}

export interface ChartPopoverHostStyleOptions {
  animated?: boolean
}

export function chartPopoverHostStyle(
  x: number,
  y: number,
  placement: ChartPopoverPlacement = 'top',
  gap: number = CHART_POPOVER_GAP,
  options: ChartPopoverHostStyleOptions = {},
): CSSProperties {
  const placementTransform =
    placement === 'bottom'
      ? `translate(-50%, ${gap}px)`
      : `translate(-50%, calc(-100% - ${gap}px))`

  if (options.animated) {
    return {
      position: 'fixed',
      left: '0',
      top: '0',
      transform: `translate3d(${x}px, ${y}px, 0) ${placementTransform}`,
      pointerEvents: 'none',
      zIndex: 9999,
    }
  }

  return {
    position: 'fixed',
    left: `${x}px`,
    top: `${y}px`,
    transform: placementTransform,
    pointerEvents: 'none',
    zIndex: 9999,
  }
}

export interface CartesianHoverContext {
  dataIndex: number
  seriesIndex: number
  value: number
  color: string
  label: string
  seriesName: string
  series: CartesianSeriesPoint[]
}

export interface CartesianSeriesPoint {
  seriesIndex: number
  name: string
  value: number | string
  color: string
}

export interface PieHoverContext {
  dataIndex: number
  name: string
  value: number
  color: string
  percent: number
}

export interface RatingsBarHoverContext {
  dataIndex: number
  name: string
  value: number
  color: string
  percent: number
  totalCount: number
}

export interface WChartPopoverConfig<TContext> {
  enabled?: boolean
  variant?: WChartPopoverVariant
  title?: (ctx: TContext) => string
  metrics?: (ctx: TContext) => WChartPopoverMetric[]
  label?: (ctx: TContext) => string
  value?: (ctx: TContext) => string | number
  valueType?: WChartPopoverValueType
  icon?: WChartPopoverIcon
  valueColor?: (ctx: TContext) => string
}

export function resolveChartPopoverProps<TContext>(
  config: WChartPopoverConfig<TContext>,
  ctx: TContext,
): Partial<WChartPopoverProps> {
  const variant: WChartPopoverVariant = config.variant ?? 'default'
  if (variant === 'compact') {
    return {
      variant,
      label: config.label?.(ctx) ?? '',
      value: config.value?.(ctx) ?? '',
      valueType: config.valueType,
      icon: config.icon,
      valueColor: config.valueColor?.(ctx) ?? '',
    }
  }
  return {
    variant,
    title: config.title?.(ctx) ?? '',
    metrics: config.metrics?.(ctx) ?? [],
  }
}
